import { hashKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/http';
import {
  fetchInterview,
  isReadable,
  submitInterview,
  type InterviewOut,
  type InterviewRead,
} from '@/api/interviews';
import { queryKeyRoots, queryKeys } from '@/api/queryKeys';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';

import { refusalOf, type Refusal } from './submissionProblems';
import type { Autosave } from './useAutosave';

interface Props {
  /** The reader's own unsubmitted interview — the branch in `index.tsx` guarantees both halves. */
  interview: InterviewOut;
  /** The viewer, because every key this write touches carries them (ADR 0006). */
  viewerId: number;
  /** The autosave, for the two things submission needs from it: the flush, and the latch. */
  autosave: Autosave;
  /** A 422: the completeness gate refused, and the fields are §6.6's to mark. */
  onRefused: (refusal: Refusal) => void;
  onClose: () => void;
}

/**
 * What this dialog is saying at the moment. `confirm` is the question; the rest are answers that are not
 * a failure — the 422 is handled by the screen behind (the fields are where it belongs), and the two 409s
 * are facts about the record rather than about the request.
 */
type Phase =
  | { kind: 'confirm' }
  /** The flush did not land, so **nothing was sent**: submitting would have frozen a stale interview. */
  | { kind: 'unsaved' }
  /** 409, and the record turned out to be submitted — in another tab, another window (ADR 0008). */
  | { kind: 'submitted'; record: InterviewRead }
  /** 409, and the record is still a draft: the candidate's data was erased (ADR 0009), or we cannot tell. */
  | { kind: 'stopped'; anonymized: boolean };

/**
 * §6.6's **Submit** — the confirmation in front of **the one irreversible act in the product**, and the
 * whole of submission behind it (`POST /api/v1/interviews/{interview_id}/submit/`).
 *
 * ⚠️ **Feedback protections #4 and #5 meet on this one click, and the order matters.** Submission sends
 * **no body** — it is an act, not a write (ADR 0008), and `submitted_at` is in no request schema in this
 * API — so what it freezes is whatever the last `PATCH` left on the server. The debounce means that can
 * be two seconds behind the screen, and a submission a second after a keystroke would freeze the *older*
 * text: irreversibly, silently, on the one act with no way back. So `autosave.flush()` is awaited
 * **first**, and a submission goes out only when the server holds every value on screen. If it does not,
 * nothing is sent and the dialog says so — the criteria for this task did not name this case, and it is
 * the reason the flush exists (`useAutosave`).
 *
 * ⚠️ **After a `200` there is no way back offered anywhere, because there is none.** No endpoint clears
 * `submitted_at`, there is no un-submit for the author, for an `Admin`, for a `Recruiter` or for a
 * superuser (the Django admin is read-only), and there is no `DELETE`. A changed opinion is an addendum
 * (ADR 0008, protection #5), which is what the confirmation says **before** the click rather than after
 * it, and `submit.test.tsx` sweeps the rendered screen for a control offering an edit, an un-submit, a
 * reopen, a withdrawal or a delete.
 *
 * ⚠️ **The `422` is not a failure and is not rendered here.** It is §6.6's completeness gate, it carries
 * **every** problem at once, and each one belongs at the field it names — so it is handed up
 * (`onRefused`) and this dialog closes, because a list of problems inside a modal is a list somebody has
 * to remember rather than fix. Nothing is toasted and nothing goes through `alert` (§4).
 *
 * ⚠️ **Both `409`s are told apart by one extra read, exactly as the autosave's are** (`useAutosave` →
 * `AutosaveRefusal`): the API answers *submitted already* (ADR 0008) and *the candidate's personal data
 * was erased* (ADR 0009) with one `{detail}` shape, and matching on the server's sentence is out of the
 * question twice over (§8, §11, and a backend string can be reworded without notice). So the interview is
 * read once and `submitted_at` decides. If that read cannot answer, the screen says the true half and
 * claims nothing further.
 */
export function SubmitDialog({ interview, viewerId, autosave, onRefused, onClose }: Props) {
  const { t } = useTranslation(['interviews', 'common', 'components']);
  const queryClient = useQueryClient();

  const [phase, setPhase] = useState<Phase>({ kind: 'confirm' });
  const interviewKey = queryKeys.interview(viewerId, interview.id);

  /**
   * ⚠️ **The four roots, and the reason is ADR 0006 rather than tidiness: the submitter's own blind gate
   * has just opened.** Every blind-filtered body this viewer holds became stale in the same instant — the
   * person card's average and latest recommendation (`person`), §6.3's row for the consideration
   * (`considerations`), every other interview of that person, which stops being the blinded shape and
   * comes back in full (`interviews`), and §6.2's outstanding-feedback count and Section 4 histogram
   * (`dashboard`). `queryKeys.interview` lives **inside** the `interviews` family precisely so one root
   * reaches every detail entry too (see its docblock).
   *
   * ⚠️ **This interview's own entry is excluded from that invalidation, and the exclusion is the point.**
   * The response *is* the readable payload with `submitted_at` populated, so it is written into the cache
   * and the screen re-renders as §6.7 **in place** — the same address, the same id, **no second `GET`**.
   * Invalidating it along with the family would refetch it immediately (it is the active query on this
   * screen), which is the second read the criterion rules out; `refetchType: 'none'` for the whole family
   * would have been the other way round, and would leave §6.7's own *Other interviews* block rendering
   * the blinded rows the submission had just opened.
   */
  const settle = (submitted: InterviewOut) => {
    queryClient.setQueryData(interviewKey, submitted);

    void queryClient.invalidateQueries({
      queryKey: queryKeyRoots.interviews,
      predicate: (query) => hashKey(query.queryKey) !== hashKey(interviewKey),
    });
    void queryClient.invalidateQueries({ queryKey: queryKeyRoots.person });
    void queryClient.invalidateQueries({ queryKey: queryKeyRoots.considerations });
    void queryClient.invalidateQueries({ queryKey: queryKeyRoots.dashboard });
  };

  const submit = useMutation<InterviewOut, ApiError, void>({
    mutationFn: () => submitInterview(interview.id),
    onSuccess: (submitted) => {
      /*
       * The write path is closed **before** the cache flips, so the unmount of the form cannot send a
       * `PATCH` at a record that has just become immutable (`useAutosave`'s flush-on-unmount would
       * otherwise fire for anything typed while the request was in the air).
       */
      autosave.stop();
      settle(submitted);
      onClose();
    },
    onError: async (error) => {
      const refusal = refusalOf(error);
      if (refusal !== null) {
        onRefused(refusal);
        onClose();
        return;
      }

      if (error.status !== 409) return;

      /*
       * The one read that tells the two 409s apart. It is deliberately **not** `fetchQuery`: writing this
       * payload into the interview's cache entry would replace the form with §6.7 and take every word on
       * screen with it, which is the one thing neither 409 may do. Nothing is stored until the reader asks
       * for it.
       */
      try {
        const record = await fetchInterview(interview.id);
        setPhase(
          record.submitted_at === null
            ? { kind: 'stopped', anonymized: true }
            : { kind: 'submitted', record },
        );
      } catch {
        setPhase({ kind: 'stopped', anonymized: false });
      }
    },
  });

  /** The reader's own step out of the *already submitted* answer: §6.7, in place, from the payload above. */
  const readSubmitted = (record: InterviewRead) => {
    if (isReadable(record)) queryClient.setQueryData(interviewKey, record);
    else void queryClient.invalidateQueries({ queryKey: interviewKey });
    onClose();
  };

  const confirm = async () => {
    // A second press starts from the question again, so an answer about the *previous* one cannot sit
    // under a request that has since been made.
    setPhase({ kind: 'confirm' });

    /*
     * ⚠️ **Everything typed on the server first, and the submission only if it got there.** See the note
     * on this component: a submission freezes what the server holds, not what is on screen.
     */
    if (!(await autosave.flush())) {
      setPhase({ kind: 'unsaved' });
      return;
    }

    submit.mutate();
  };

  /**
   * The sentence for a failure that is neither a refusal nor a 409 — offline, throttled, or a 5xx.
   *
   * ⚠️ **It says nothing was frozen, and that is a claim this route can make**: the submission is one
   * transaction (`apps.interviews.services.submit_interview`), so a status means it rolled back. Status 0
   * is the exception the shared sentence already covers — no answer arrived, so nothing is known and
   * nothing is queued (`components:state.offlineWrite`, BUG-017's split).
   */
  const failure = (() => {
    if (!submit.error || phase.kind !== 'confirm') return null;
    if (refusalOf(submit.error) !== null || submit.error.status === 409) return null;
    if (submit.error.isOffline) return t('components:state.offlineWrite');
    if (submit.error.isThrottled) return t('components:state.throttled');
    return t('interviews:filling.submit.failed');
  })();

  if (phase.kind === 'submitted') {
    return (
      <Dialog
        open
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        title={t('interviews:filling.submit.submittedTitle')}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common:close')}
            </Button>
            <Button onClick={() => readSubmitted(phase.record)}>
              {t('interviews:filling.submit.read')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3 text-sm text-ink-900">
          <p>{t('interviews:filling.submit.submittedLead')}</p>
          <p>{t('interviews:filling.submit.kept')}</p>
        </div>
      </Dialog>
    );
  }

  if (phase.kind === 'stopped') {
    return (
      <Dialog
        open
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        title={t('interviews:filling.submit.stoppedTitle')}
        footer={
          <Button variant="secondary" onClick={onClose}>
            {t('common:close')}
          </Button>
        }
      >
        <div className="flex flex-col gap-3 text-sm text-ink-900">
          <p>
            {phase.anonymized
              ? t('interviews:filling.submit.anonymized')
              : t('interviews:filling.submit.stoppedUnknown')}
          </p>
          <p>{t('interviews:filling.submit.kept')}</p>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // `Esc` and the overlay cancel, and cancelling sends **nothing**: Radix owns the focus trap and
        // the key handling (§9), and the request is made by the button below and by nothing else.
        if (!next) onClose();
      }}
      title={t('interviews:filling.submit.title')}
      description={t('interviews:filling.submit.warning')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          {/*
           * The primary action, and **not** `danger`: submitting is what this screen is for, and §4
           * reserves the outlined `negative` button for destruction. What makes it consequential is
           * stated in words above it rather than in a colour.
           */}
          <Button isLoading={submit.isPending} onClick={() => void confirm()}>
            {t('interviews:filling.submit.confirm')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm text-ink-900">
        {/*
         * ⚠️ **The other half of what this click does, and ADR 0006 is why it belongs in front of it.**
         * Submission opens the submitter's **own** blind gate: this candidate's other feedback becomes
         * readable to them in the same instant, and theirs becomes readable to whoever has already
         * submitted. It is not a warning — it is how the product works — but a person about to take the
         * one irreversible act should not discover it afterwards.
         */}
        <p>{t('interviews:filling.submit.opensGate')}</p>

        {phase.kind === 'unsaved' && (
          <p role="alert" className="text-negative">
            {t('interviews:filling.submit.unsaved')}
          </p>
        )}

        {failure && (
          <p role="alert" className="text-negative">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}
